import MarkerToken from './map/MarkerToken'

const DEMO_CARDS = [
  {
    name: 'The Ivy Room',
    subtitle: '🔥 12 recent viewers',
    counts: [3, null, null, null, 12] as (number | null)[],
    pinned: true,
  },
  {
    name: 'Corner Bar',
    subtitle: 'Happy hour until 7pm',
    counts: [5, null, null, null, null] as (number | null)[],
    pinned: false,
  },
  {
    name: 'Rooftop Lounge',
    subtitle: '0.3 mi · NE',
    counts: [1, null, 2, null, null] as (number | null)[],
    pinned: false,
  },
] as const

const SpotlightTickerGuide = () => (
  <div className="spotlight-ticker-guide">
    <div className="spotlight-ticker-guide__intro">
      <p>
        The spotlight ticker is the horizontal strip of venue cards at the bottom of the mobile map. The app loads it
        from <code>GET /user/venues/spotlighted</code> using the user&apos;s location. These settings are{' '}
        <strong>server-only</strong> — they are not sent in <code>/me</code> and take effect as soon as you save.
      </p>
      <ol className="spotlight-ticker-guide__pipeline">
        <li>
          <strong>Geography</strong> — only venues inside the search-radius bounding box around the user are considered.
        </li>
        <li>
          <strong>Availability</strong> — venues outside their <code>available_from</code> / <code>available_to</code>{' '}
          window are excluded.
        </li>
        <li>
          <strong>Eligibility</strong> — a venue must match <em>at least one</em> enabled rule (check-ins, recent peeks,
          logo, active special / visit plan).
        </li>
        <li>
          <strong>Current check-in</strong> — optionally force the user&apos;s checked-in venue into the candidate set,
          even if it failed geography or eligibility.
        </li>
        <li>
          <strong>Ranking</strong> — sort candidates by activity priority, then recent viewers, then distance.
        </li>
        <li>
          <strong>Limit</strong> — return the top <em>N</em> venues. Each card shows a logo token, name, and a detail
          line (special text → recent viewers → direction/distance).
        </li>
      </ol>
    </div>

    <div className="spotlight-ticker-preview" aria-hidden>
      <div className="spotlight-ticker-preview__header">
        <span className="spotlight-ticker-preview__label">Example (mobile map)</span>
        <span className="spotlight-ticker-preview__hint">Cards scroll when they overflow the screen width</span>
      </div>
      <div className="spotlight-ticker-preview__frame">
        <div className="spotlight-ticker-preview__map" />
        <div className="spotlight-ticker-preview__cards">
          {DEMO_CARDS.map(card => (
            <div
              key={card.name}
              className={
                'spotlight-ticker-preview__card' + (card.pinned ? ' spotlight-ticker-preview__card--pinned' : '')
              }
            >
              <MarkerToken name={card.name} counts={card.counts} />
              <span className="spotlight-ticker-preview__card-body">
                <span className="spotlight-ticker-preview__card-name">{card.name}</span>
                <span className="spotlight-ticker-preview__card-subtitle">{card.subtitle}</span>
              </span>
              {card.pinned ? <span className="spotlight-ticker-preview__pin-badge">Your check-in</span> : null}
            </div>
          ))}
        </div>
      </div>
      <p className="spotlight-ticker-preview__caption">
        Illustrative order: pinned current check-in first, then venues ranked by your activity priority. Detail lines
        prefer venue <code>spotlight_content</code>, then the recent-viewers flame, then distance/direction.
      </p>
    </div>

    <details className="spotlight-ticker-guide__details">
      <summary>Setting reference</summary>
      <dl className="spotlight-ticker-guide__glossary">
        <dt>Venues returned</dt>
        <dd>
          Maximum number of venue cards the API returns after ranking (default 8). The mobile client may show fewer if
          the screen is narrow; extra cards appear when the user scrolls the ticker.
        </dd>

        <dt>Search radius (degrees)</dt>
        <dd>
          Half-width of the latitude/longitude bounding box centered on the user. 0.5° ≈ 55 km at the equator (slightly
          less at higher latitudes). Smaller values keep the ticker hyper-local; larger values surface venues farther
          away but may dilute relevance.
        </dd>

        <dt>Recent views window (days)</dt>
        <dd>
          How far back peeks count toward the <strong>Recent viewers</strong> eligibility rule and the{' '}
          <code>interestedUsers</code> / <code>peeks</code> fields on each venue. Also used as a tie-breaker in
          ranking after activity priorities.
        </dd>

        <dt>Eligibility rules</dt>
        <dd>
          Rules are combined with <strong>OR</strong> logic — matching any enabled rule is enough. Count rules require
          at least the configured minimum; flag rules are pass/fail. Disabling all rules blocks save.
        </dd>

        <dt>Current check-in → Always include</dt>
        <dd>
          When the user is checked in, their venue is added to candidates even if it is outside the search radius or
          fails every eligibility rule. Does not bypass availability dates.
        </dd>

        <dt>Current check-in → Pin first</dt>
        <dd>
          Sorts the checked-in venue ahead of all activity-based ranking. If both pin-first and always-include are off,
          the current venue competes like any other candidate.
        </dd>

        <dt>Ranking priority</dt>
        <dd>
          Drag to set which activity signal wins when comparing two venues. Position 0 is highest. Within each step,
          venues with activity rank above those without; when both have activity, higher counts win. The{' '}
          <code>events</code> slot uses RSVPs <strong>plus</strong> visit plans for today. After your ordered list,
          the server always applies <em>recent viewers</em> then <em>distance</em> as final tie-breakers.
        </dd>
      </dl>
    </details>
  </div>
)

export default SpotlightTickerGuide
